import React, { useState, useCallback, forwardRef, useEffect, useRef } from 'react'
import Spinner from 'GeneralUI/Spinner/Spinner.jsx'
import SpinnerErrors from 'GeneralUI/Spinner/SpinnerErrors.jsx'
import SpinnerSuccess from 'GeneralUI/Spinner/SpinnerSuccess.jsx'
import DivButton from 'GeneralUI/DivButton/DivButton.jsx'

const withSpinnerFeedback = (type = 'window') => {
  return WrappedComponent => {
    const WithSpinnerFeedback = forwardRef((props, ref) => {
      const [spinner, setSpinner] = useState(false)
      const [spinnerError, setSpinnerError] = useState(null)
      const [spinnerSuccess, setSpinnerSuccess] = useState(null)
      const [spinnerComplete, setSpinnerComplete] = useState(false)
      const [windowAnimationClass, setWindowAnimationClass] = useState('')
      const [transactionIds, setTransactionIds] = useState([])
      const [errorConfig, setErrorConfig] = useState()
      const [successConfig, setSuccessConfig] = useState()
      const [transactionSuccessCallback, setTransactionSuccessCallback] = useState(() => null)
      const [transactionErrorCallback, setTransactionErrorCallback] = useState(() => null)
      const spinnerActive = spinner || spinnerError || spinnerSuccess
      const timer = useRef()
      const endSpinnerWithErrorRef = useRef()
      const endSpinnerWithSuccessRef = useRef()
      
      // this is called by the spinner success and failure handlers but due to the way those
      // are passed around the app they frequently are called as stale references. rather than 
      // building this into those functions, i'm just passing a ref to a function that lives here
      // to avoid stale state references when updating these ids.
      const removeTransactionId = useCallback((removeId) => {
        const newIds = transactionIds.filter(id => id != removeId)
        setTransactionIds(newIds)
        }, [transactionIds])

      const removeTransactionIdRef = useRef(removeTransactionId)

      useEffect(() => {
        removeTransactionIdRef.current = removeTransactionId
      }, [removeTransactionId])

      const clearHandler = useCallback(() => {
        setSpinner(false)
        setSpinnerError(null)
        setSpinnerSuccess(null)
        setSpinnerComplete(true)
      }, [])

      // genrate a random transaction_id for the caller to send in the api call and store
      // it in the tracking array
      const getSpinnerTransaction = useCallback(() => {
        const transactionId = Math.floor(Math.random() * 9999999);
        setTransactionIds(old => [...old, transactionId])
        return `transaction_id=${transactionId}`
      }, [])

      const startSpinner = useCallback((args = {}) => {
        const {successCallback = () => null, errorCallback = () => null} = args
        setTransactionSuccessCallback(() => successCallback)
        setTransactionErrorCallback(() => errorCallback)
        setSpinner(true)
      }, [])

      const privateEndSpinnerWithSuccess = useCallback(customConfig => {
        const config = { autoCloseDelaySec: 2, ...customConfig }
        setSpinnerSuccess(config)
        setSpinner(false)
      }, [])

      const publicEndSpinnerWithSuccess = useCallback((customConfig = {}) => {
        if(transactionIds.length > 0 && customConfig.response?.data?.transaction_id) {
          setTransactionIds(old => old.filter(id => id != customConfig.response.data.transaction_id))
          // unlike the error handler, because all array api calls should be the same type, they should have the
          // same success outcome/messaging so just constantly updating the success config with the most recent
          // success is a valid thing to do.
          setSuccessConfig(customConfig)
        } else {
          privateEndSpinnerWithSuccess(customConfig)
        }
      }, [transactionIds])

      useEffect(() => {
        endSpinnerWithSuccessRef.current = publicEndSpinnerWithSuccess
      }, [publicEndSpinnerWithSuccess])

      const endSpinnerWithSuccess = useCallback((customConfig = {}) => {
        endSpinnerWithSuccessRef.current(customConfig)
      }, [transactionIds])

      const privateEndSpinnerWithError = useCallback((customConfig) => {
        const clearError = (closeHandler, clearHandler) => {
          return (
            <div className='spinner-bottom-content'>
              <
                DivButton
                className = 'btn btn-default'
                onClick={closeHandler}
              >
                {'Clear Error'}
              </DivButton>
            </div>
          )
        }
        const config = { bottomContent: clearError, ...customConfig }
        setSpinnerError(config)
        setSpinner(false)
      }, [])

      const publicEndSpinnerWithError = useCallback((customConfig = { errors: 'An unknown error ocurred.' }) => {
        if(transactionIds.length > 0 && customConfig.response?.data?.transaction_id) {
          setTransactionIds(old => old.filter(id => id != customConfig.response.data.transaction_id))
          // TBD: in this case we are handling an array of api calls. In the future it would be good to store the errors
          // from each response so that at the end all errors could be displayed. for now we are just tracking the last one
          // that errored.
          setErrorConfig(customConfig)
        } else {
          privateEndSpinnerWithError(customConfig)
        }
      }, [transactionIds])

      useEffect(() => {
        endSpinnerWithErrorRef.current = publicEndSpinnerWithError
      }, [publicEndSpinnerWithError])

      const endSpinnerWithError = useCallback((customConfig = { errors: 'An unknown error ocurred.' }) => {
        endSpinnerWithErrorRef.current(customConfig)
      }, [])

      useEffect(() => {
        if(transactionIds.length === 0 && spinnerActive) {
          // if the last of the transactions has completed we can finish.
          if(errorConfig) {
            // if we stored an error config, we need to end with errors
            privateEndSpinnerWithError(errorConfig)
            transactionErrorCallback(errorConfig)
          } else {
            privateEndSpinnerWithSuccess(successConfig)
            transactionSuccessCallback(successConfig)
          }
        }
      },[transactionIds]) // only run this when transaction ids change

      const spinnerContentSelector = useCallback((contentCloseHandler = () => null) => {
        const closeHandler = () => {
          clearHandler()
          contentCloseHandler()
        }
        if (spinner) {
          return <Spinner/>
        } else if (spinnerSuccess) {
          return <SpinnerSuccess closeHandler={closeHandler} clearHandler={clearHandler} config={spinnerSuccess}/>
        } else if (spinnerError) {
          return <SpinnerErrors closeHandler={closeHandler} clearHandler={clearHandler} config={spinnerError} startSpinner={startSpinner}/>
        }
      }, [spinner, spinnerSuccess, spinnerError])

      useEffect(() => {
        if (type === 'window') {
          // manage adding the animate.css classes to the window for the spinnerSuccess/Fail functionality
          if (!spinnerActive && windowAnimationClass === 'animate__animated animate__fadeOut') {
            setWindowAnimationClass('animate__animated animate__fadeIn')
            // remove the class after 500ms... this should be enough time for the animation to finish but
            // leaving it causes everything in the div to have a blurry appearance.
            timer.current = setTimeout(() => setWindowAnimationClass(''), 500)
          } else if (spinnerActive) {
            setWindowAnimationClass('animate__animated animate__fadeOut')
          }
        } else {
          if (spinnerActive) {
            setWindowAnimationClass('hidden')
          } else {
            setWindowAnimationClass('')
          }
        }
      }, [spinnerActive, windowAnimationClass])

      useEffect(() => {
        return () => {
          // componentWillUnmount
          if (timer.current && timer.current.clearTimeout) {
            timer.current.clearTimeout()
          }
        }
      }, [])

      const newProps = {
        ...props,
        spinnerActive,
        spinnerComplete,
        startSpinner,
        stopSpinner: clearHandler,
        endSpinnerWithSuccess,
        endSpinnerWithError,
        spinnerContentSelector,
        getSpinnerTransaction
      }
      return (
        <React.Fragment>
          {spinnerContentSelector(
            type === 'modal' ? props.closeSelf : () => null
          )}
          <div className={`spinner-feedback-wrapper ${windowAnimationClass} `}>
            <WrappedComponent ref={ref} {...newProps} />
          </div>
        </React.Fragment>
      )
    })
    return WithSpinnerFeedback
  }
}

export default withSpinnerFeedback
